import { format, parseISO } from "date-fns"
import { useState, useMemo, useEffect } from "react"
import IKanbanBoardProductionSchemaProps from "../../../Components/KanbanBoardProduction/interfaces/kanban-board-production-schema.interface"
import { Environment } from "../../../Config/constants"
import { ServiceOrderStatusEnum } from "../../../Services/api/service-orders/service-order.interface"
import { BillProductOrderOrdersUseCase } from "../../../Usecase/Orders/bill-products-order-usecase"
import { ListOrdersUseCase } from "../../../Usecase/Orders/list-orders-usecase"
import { useToast } from "../../../Hooks/ToasContext"
import { ShowOrdersUseCase } from "../../../Usecase/Orders/show-order-usecase"

import { ptBR } from 'date-fns/locale';
import { FormatMoney } from "../../../utils/formatMoney"
import { buildSaleFromOrder } from "../../../utils/build-sale-from-order"
import { enrichOrderFromList, normalizeOrderApiResponse } from "../../../utils/normalize-order-api-response"
import IKanbanBoardFinancialSchemaProps from "../../../Components/KanbanBoardFinancial/interfaces/kanban-board-financial-schema.interface"
import { ShowOmieOrderDetailsUseCase } from "../../../Usecase/Orders/show-omie-order-details-usecase"
import { UpdateOrdersUseCase } from "../../../Usecase/Orders/update-order-usecase"
import { ISellerEntity } from "../../../Services/api/sellers/sellers.interface"
import { useNavigate } from "react-router-dom"
import { ICustomerEntity } from "../../../Services/api/customers/customers.interface"
import { IOrderEntity, IOrdersRegisteredOnOmieEntity, IBillProductOrderPayload, TBillingFlow } from "../../../Services/api/orders/orders.interface"
import { IAddressEntity } from "../../../Services/api/addresses/addresses.interface"
import { SubmitHandler, useForm } from "react-hook-form"
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import api from "../../../Services/api"


interface IUseDetailsFinancialProps {
  listOrdersUseCase: ListOrdersUseCase
  billProductOrderOrdersUseCase: BillProductOrderOrdersUseCase
  showOrdersUseCase: ShowOrdersUseCase
  orderId: string;
  showOmieOrderDetailsUseCase: ShowOmieOrderDetailsUseCase
  updateOrdersUseCase: UpdateOrdersUseCase
}

interface IUpdateObservationPayload {
  observations: string
}

interface IOmieCompany {
  id: string
  name: string
  alias: string
  cnpj?: string
  active: boolean
  canIssueInvoice: boolean
  canReceivePayment: boolean
  settings?: Record<string, unknown>
  hasCredentials?: boolean
}

interface ICompanyOption {
  label: string
  value: string | null
}

const UpdateObservationSchemaValidation = Yup.object().shape({
  observations: Yup.string(),
});

export interface Task {
  id: string;
  content: {
    os: string;
    items: any; // ajuste o tipo conforme sua necessidade
    type?: 'checkbox' | 'click' | 'navigate'
  };
}

export interface ISaleEntity {
  id: string;
  createdAt: string;
  customer: ICustomerEntity;
  total: string;
  shippingAddress: IAddressEntity;
  billingAddress: IAddressEntity;
  products: {
    id: string;
    name: string;
    quantity: number;
    variant: string;
    sku: string;
    price_unit: string;
    total: string;
    image: string;
    hcpCurrentFlow?: string | null;
    hcpCurrentFlowStatusId?: string | null;
    hcpCurrentFlowCode?: number | null;
    hcpCurrentFlowStatusSequence?: number | null;
    hcpCurrentFlowDisplayCode?: string | null;
    hcpCurrentFlowStatusName?: string | null;
    hcpCurrentFlowStartedAt?: string | null;
    hcpCurrentFlowDeadline?: string | null;
    hcpFinished?: boolean | null;
    hcpPreServiceOrderId?: string | null;
    productOrderCode?: string | null;
  }[]
}

const tasks: Record<string, Task> = {};
export function useDetailsFinancial({ listOrdersUseCase, billProductOrderOrdersUseCase, showOrdersUseCase, orderId, showOmieOrderDetailsUseCase, updateOrdersUseCase }: IUseDetailsFinancialProps){
    const [loading, setLoading] = useState(true)
    const [orderData, setOrderData] = useState<IOrderEntity>()
    const [kanbanFinancialSchema, setKanbanFinancialSchema] = useState<IKanbanBoardFinancialSchemaProps>()
    const [kanbanProductionSchema, setKanbanProductionSchema] = useState<IKanbanBoardProductionSchemaProps>()
    const [productsToBill, setProductsToBill] = useState<string[]>([])
    const [omieOrderData,setOmieOrderData] = useState<IOrdersRegisteredOnOmieEntity | undefined>(undefined)
    const [showModalOrderWithoutSeller, setShowModalOrderWithoutSeller] = useState(false)
    const [sellerSelected, setSellerSelected] = useState<ISellerEntity>()
    const [showCustomersModal, setShowCustomersModal] = useState(false)
    const [omieCompanies, setOmieCompanies] = useState<IOmieCompany[]>([])
    const [selectedBillingCompanyId, setSelectedBillingCompanyId] = useState('')
    const [selectedReceivingCompanyId, setSelectedReceivingCompanyId] = useState<string | null>(null)
    const navigate = useNavigate()
    const { handleSubmit, control, setValue, register } = useForm<IUpdateObservationPayload>({
      resolver: yupResolver(UpdateObservationSchemaValidation),
  });
    const { showToast } =  useToast()

    const transformProductionData = (dados: IOrderEntity) => {
      
      const tasks: Record<string, Task> = {};
      const initialOS : any = [];
      const onProductionOS : any = [];
      const finishedOS : any = [];
      
      dados.products.forEach((item, index) => {
          // const taskId = `task-${index + 1}`;
          const taskId = item.id;
          tasks[taskId] = {
              id: taskId,
              content: {
                  os: `Produto - ${item.product.name}`,
                  items: item.product
              }
          };

          if(item.status === ServiceOrderStatusEnum.INITIAL){
            initialOS.push(taskId);
          } else if ([ServiceOrderStatusEnum.PROCESSING, ServiceOrderStatusEnum.ON_GOING, ServiceOrderStatusEnum.PRODUCTION].includes(item.status)){
            onProductionOS.push(taskId)
          } else {
            finishedOS.push(taskId)
          }
          
      });

      const result = {
          tasks,
          columns: {
            [ServiceOrderStatusEnum.INITIAL]: {
                  id: ServiceOrderStatusEnum.INITIAL,
                  title: 'Aguardando Início',
                  taskIds: initialOS,
              },
              [ServiceOrderStatusEnum.PROCESSING]: {
                  id: ServiceOrderStatusEnum.PROCESSING,
                  title: 'Em Produção',
                  taskIds: onProductionOS,
              },
              [ServiceOrderStatusEnum.PRODUCED]: {
                  id: ServiceOrderStatusEnum.PRODUCED,
                  title: 'Produzido',
                  taskIds: finishedOS,
              },
          },
          columnOrder: [ServiceOrderStatusEnum.INITIAL, ServiceOrderStatusEnum.PROCESSING, ServiceOrderStatusEnum.PRODUCED],
      };

      return result;
    }

    const transformFinancialData = (dados: IOrderEntity) => {
      
      const tasks: Record<string, Task> = {};
      const readyToBilled : any = [];
      const billedOrders : any = [];
      
      dados?.products?.forEach((item, index) => {
          // const taskId = `task-${index + 1}`;
          const taskId = item.id;
          tasks[taskId] = {
              id: taskId,
              content: {
                  os: `Produto ${item.product.name}`,
                  items: item.product,
                  type: 'checkbox'
              }
          };

          if(item.status === ServiceOrderStatusEnum.PRODUCED){
            readyToBilled.push(taskId);
          } 
          
      });

      dados?.ordersRegisteredOnOmie?.forEach((item, index) => {
        // const taskId = `task-${index + 1}`;
        const taskId = item.id
        tasks[taskId] = {
            id: taskId,
            content: {
                os: `Pedido OMIE: ${item?.omieOriginalOrderId}`,
                items: item.serviceOrders,
                type: 'navigate'
            }
        };

        billedOrders.push(taskId);
        
    });



      const result = {
          tasks,
          columns: {
            [ServiceOrderStatusEnum.PRODUCED]: {
                  id: ServiceOrderStatusEnum.PRODUCED,
                  title: 'Aguardando Faturamento',
                  taskIds: readyToBilled,
                  checkAll: true
              },
              [ServiceOrderStatusEnum.BILLED]: {
                  id: ServiceOrderStatusEnum.BILLED,
                  title: 'Faturado',
                  taskIds: billedOrders,
              },
             
          },
          columnOrder: [ServiceOrderStatusEnum.PRODUCED, ServiceOrderStatusEnum.BILLED],
      };

      return result;
    }

    const handleShowOrder = async (orderId: string) => {
       
      try {
        setLoading(true)
        
        const orderResult = await showOrdersUseCase.handle(orderId);
  
        if (orderResult.isFailure) {
    
          showToast(String(orderResult?.error?.error), 'error');
          return;
        }
  
        const result = orderResult.getValue();

        if(result?.id){
          const enriched = await enrichOrderFromList(result, listOrdersUseCase)
  
          const normalizeSchema : IKanbanBoardFinancialSchemaProps = transformFinancialData(enriched)
          
          setKanbanFinancialSchema(normalizeSchema)
          const normalizeOrder = {
            ...enriched,
            products: (enriched.products ?? []).map((i: any) => ({
              ...i,
              productionDueDate: i?.serviceOrder?.dueDate ? format(parseISO(i?.serviceOrder?.dueDate), 'dd/MM/yyyy') : 'Não informado',
              artApprovedDateLabel: (() => {
                if (!i?.artApprovedDate) return 'Não informado';
                try {
                  return format(parseISO(i.artApprovedDate), 'dd/MM/yyyy');
                } catch {
                  return String(i.artApprovedDate);
                }
              })(),
            }))
          }
          const normalizedOrder = normalizeOrderApiResponse(normalizeOrder)
          setOrderData(normalizedOrder)
          setSelectedBillingCompanyId(normalizedOrder.omieBillingCompanyId ?? '')
          setSelectedReceivingCompanyId(normalizedOrder.omieReceivingCompanyId ?? null)

          if(enriched?.observations){
            setValue('observations', enriched.observations)
          }
        }
  
      } catch (error) {
        console.error("error handleShowOrder function: ", error)
      } finally {
        setLoading(false);
      }
      };

    const handleLoadOmieCompanies = async () => {
      try {
        const response = await api.get('/backoffice/omie-companies')
        const data = response.data
        const companies = Array.isArray(data) ? data : data?.data ?? data?.items ?? data?.results ?? []
        setOmieCompanies(companies)
      } catch (error) {
        console.error("error handleLoadOmieCompanies function: ", error)
        showToast('Erro ao carregar empresas Omie', 'error')
      }
    }

    useEffect(() => {
      handleLoadOmieCompanies()
    }, [])

    const handleDetailsOmieOrder = async (orderId?: string) => {
      
      try {
        setLoading(true)
        
        const orderResult = await showOmieOrderDetailsUseCase.handle(String(orderId));
  
        if (orderResult.isFailure) {
    
          showToast(String(orderResult?.error?.error), 'error');
          return;
        }
  
        const result = orderResult.getValue();
  
        if(result?.id){
          setOmieOrderData(result)
        }
  
      } catch (error) {
        console.error("error handleShowOrder function: ", error)
      } finally {
        setLoading(false);
      }
    };
   

    const billProductsOrder = async () => {

      if(!productsToBill?.length){
        showToast(String('Selecione pelo menos 1 produto para faturar'), 'error');
        return;
      }
         
      try {
        setLoading(true)

        const payload : IBillProductOrderPayload = {
          orderId,
          productsIds: productsToBill
        }
        
        const billResult = await billProductOrderOrdersUseCase.handle(payload);

        if (billResult.isFailure) {
     
          showToast(String(billResult?.error?.error), 'error');
          return;
        }

        showToast('Produtos faturados com sucesso', 'success');
        handleShowOrder(orderId)
      } catch (error) {
        console.error("error billProductsOrder function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleUpdateBillingFlow = async (billingFlow: TBillingFlow) => {
      try {
        setLoading(true)

        const result = await updateOrdersUseCase.handle({ orderId, billingFlow });

        if (result.isFailure) {
          showToast(String(result?.error?.error), 'error');
          return;
        }

        setOrderData(prev => prev ? { ...prev, billing_flow: billingFlow, billingFlow } : prev)
        showToast('Fluxo de faturamento atualizado com sucesso', 'success');
        handleShowOrder(orderId)
      } catch (error) {
        console.error("error handleUpdateBillingFlow function: ", error)
        showToast('Erro ao atualizar fluxo de faturamento', 'error');
      } finally {
        setLoading(false);
      }
    };

    const handleUpdateOrderOmieCompanies = async () => {
      if (!selectedBillingCompanyId) {
        showToast('Empresa de faturamento é obrigatória', 'warning')
        return
      }

      try {
        setLoading(true)

        const result = await updateOrdersUseCase.handle({
          orderId,
          omieBillingCompanyId: selectedBillingCompanyId,
          omieReceivingCompanyId: selectedReceivingCompanyId,
        });

        if (result.isFailure) {
          showToast(String(result?.error?.error), 'error');
          return;
        }

        showToast('Empresas Omie atualizadas com sucesso', 'success');
        handleShowOrder(orderId)
      } catch (error) {
        console.error("error handleUpdateOrderOmieCompanies function: ", error)
        showToast(error.response?.data?.error ?? 'Erro ao atualizar empresas Omie', 'error');
      } finally {
        setLoading(false);
      }
    };

    const handleUpdateOrderWithoutSeller = async () => {
         
      try {
        setLoading(true)

        const result = await updateOrdersUseCase.handle({ sellerId: "0", orderId});

        if (result.isFailure) {
     
          showToast(String(result?.error?.error), 'error');
          return;
        }

        showToast('Pedido atualizado com sucesso', 'success');
        handleShowOrder(orderId)
        setShowModalOrderWithoutSeller(false)
      } catch (error) {
        console.error("error handleUpdateOrderWithoutSeller function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleUpdateOrder = async (seller: ISellerEntity) => {
         
      try {
        setLoading(true)

        const result = await updateOrdersUseCase.handle({ sellerId: seller?.id, orderId});

        if (result.isFailure) {
     
          showToast(String(result?.error?.error), 'error');
          return;
        }

        showToast('Pedido atualizado com sucesso', 'success');
        handleShowOrder(orderId)
        setShowCustomersModal(false)
      } catch (error) {
        console.error("error handleUpdateOrderWithoutSeller function: ", error)
      } finally {
        setLoading(false);
      }
    };

   

  const sale = useMemo(() => {
    if (!orderData?.id) return {} as ISaleEntity
    return buildSaleFromOrder(orderData)
  }, [orderData])

  const handleSelectProductToBill = (productId?: string) => {
      if(!productId) return
      const exists = productsToBill.find(i => i === productId)

      if(exists) {
        const normalize = productsToBill.filter(i => i !== productId)
        setProductsToBill(normalize)
      } else {
        setProductsToBill(prev => [...prev, productId])
      }
  }

  const handleSelectAllProductsToBill = () => {
    if (productsToBill?.length === orderData?.products?.length) {
      setProductsToBill([])
    } else {
      setProductsToBill(orderData?.products?.map(i => i.id) || [])
    }
  }

  const handleSelectSeller = (seller: ISellerEntity) => {
    handleUpdateOrder(seller)
   
  }

  const billingCompanyOptions = useMemo<ICompanyOption[]>(() => {
    return omieCompanies
      .filter(company => company.active && company.canIssueInvoice)
      .map(company => ({ label: company.name, value: company.id }))
  }, [omieCompanies])

  const receivingCompanyOptions = useMemo<ICompanyOption[]>(() => {
    return [
      { label: 'Padrão', value: null },
      ...omieCompanies
        .filter(company => company.active && company.canReceivePayment)
        .map(company => ({ label: company.name, value: company.id })),
    ]
  }, [omieCompanies])

  const handleUpdateObservations: SubmitHandler<IUpdateObservationPayload> = async (values) => {
    
    try {
      setLoading(true)

      const payload = await updateOrdersUseCase.handle({ orderId, observations: values.observations })

      if (payload.isFailure) {
        
        showToast(String(payload?.error?.error), 'error');
        return;
      }

      showToast('Observação atualizada com sucesso', 'success')
      handleShowOrder(orderId)

    } catch (error) {
        console.error("error: ", error)
        showToast(error.response?.data?.error ?? 'Erro. Por favor, tente novamente', 'error');
      // toast({
      //   position: 'top-right',
      //   render: () => <ToastCustom type='fail' title='Erro' description={error.response?.data?.error ?? 'Erro ao logar. Por favor, tente novamente'} />,
      // });
    } finally {
      setLoading(false);
    }
  };


    return {
        loading,
        billProductsOrder,
        handleShowOrder,
        orderData,
        kanbanFinancialSchema,
        kanbanProductionSchema,
        sale,
        handleSelectProductToBill,
        productsToBill,
        handleDetailsOmieOrder,
        omieOrderData,
        setOmieOrderData,
        handleSelectAllProductsToBill,
      
        showModalOrderWithoutSeller,
        setShowModalOrderWithoutSeller,
        handleUpdateOrderWithoutSeller,
        sellerSelected,
        showCustomersModal,
        handleSelectSeller,
        setShowCustomersModal,
        navigate,
        control,
        handleUpdateObservations,
        handleSubmit,
        register,
        handleUpdateBillingFlow,
        billingCompanyOptions,
        receivingCompanyOptions,
        selectedBillingCompanyId,
        setSelectedBillingCompanyId,
        selectedReceivingCompanyId,
        setSelectedReceivingCompanyId,
        handleUpdateOrderOmieCompanies
        
        
    }
}
